import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook for server-side tables that handles AG Grid event synchronization
 *
 * This hook manages the sync between AG Grid's internal state and external state.
 * It does NOT handle URL params - use useServerSideSearchParams for that.
 *
 * @param {Object} options
 * @param {Object} options.filters - Current filter state
 * @param {string} options.sortBy - Current sort column
 * @param {string} options.sortOrder - Current sort order ('asc'|'desc')
 * @param {number} options.page - Current page number
 * @param {number} options.pageSize - Current page size
 * @param {string[]} options.excludeFilters - Filter keys to exclude from AG Grid sync (e.g., ['searchText'])
 * @param {Function} options.onFiltersChange - Called when AG Grid filters change
 * @param {Function} options.onSortChange - Called when AG Grid sort changes
 * @param {Function} options.onPaginationChange - Called when AG Grid pagination changes
 */
export function useServerSideTable({
  filters = {},
  sortBy = '',
  sortOrder = 'asc',
  page = 1,
  pageSize = 25,
  excludeFilters = [],
  onFiltersChange,
  onSortChange,
  onPaginationChange,
}) {
  const tableRef = useRef(null);
  const isSettingFilterModelRef = useRef(false);
  const isSettingPaginationRef = useRef(false);

  // Build AG Grid filter model from external filter state
  const buildFilterModel = useCallback(() => {
    const filterModel = {};
    Object.entries(filters).forEach(([key, value]) => {
      // Skip excluded filters (e.g., searchText which isn't a column filter)
      if (excludeFilters.includes(key)) return;

      if (Array.isArray(value) && value.length > 0) {
        filterModel[key] = { values: value, filterBlanks: false };
      } else if (!Array.isArray(value) && value !== '' && value != null) {
        // != null checks for both null and undefined
        filterModel[key] = { value };
      }
    });
    return filterModel;
  }, [filters, excludeFilters]);

  // Sync AG Grid filter model when external filters change
  useEffect(() => {
    const api = tableRef.current?.getApi?.();
    if (api) {
      const filterModel = buildFilterModel();
      isSettingFilterModelRef.current = true;

      // Update AG Grid's filter UI to match external state
      api.setFilterModel(filterModel);

      // NOTE: We do NOT call api.onFilterChanged() here because:
      // - This is manual server-side filtering (Community Edition approach)
      // - setFilterModel() only updates the filter UI state
      // - The actual filtering happens on the server via API calls
      // - Calling onFilterChanged() would trigger client-side filtering (incorrect for our use case)
      // - We're NOT using Enterprise "Server-Side Row Model" feature

      // Use Promise to clear flag after all synchronous event handlers

      setTimeout(() => {
        isSettingFilterModelRef.current = false;
      }, 0);
    }
  }, [buildFilterModel]);

  // Sync AG Grid sort when external sort changes
  useEffect(() => {
    const api = tableRef.current?.getApi?.();
    if (api && sortBy) {
      const columnState = api.getColumnState();
      const needsUpdate = !columnState.some(col => col.colId === sortBy && col.sort === sortOrder);

      if (needsUpdate) {
        api.applyColumnState({
          state: [{ colId: sortBy, sort: sortOrder }],
          defaultState: { sort: null },
        });
      }
    }
  }, [sortBy, sortOrder]);

  // AG Grid event handlers
  const handleGridReady = useCallback(
    event => {
      // Sync initial filter/sort state to AG Grid's UI
      isSettingFilterModelRef.current = true;
      isSettingPaginationRef.current = true;
      const filterModel = buildFilterModel();

      // Set filter UI state (doesn't trigger client-side filtering)
      event.api.setFilterModel(filterModel);

      // Set sort UI state
      if (sortBy) {
        event.api.applyColumnState({
          state: [{ colId: sortBy, sort: sortOrder }],
          defaultState: { sort: null },
        });
      }

      // Note: Pagination state is managed via props, not API calls
      setTimeout(() => {
        isSettingFilterModelRef.current = false;
        isSettingPaginationRef.current = false;
      }, 0);
    },
    [buildFilterModel, sortBy, sortOrder],
  );

  const handleFilterChanged = useCallback(
    event => {
      // Skip if this is a programmatic filter model sync
      if (isSettingFilterModelRef.current) return;

      const filterModel = event.api.getFilterModel();

      // Convert AG Grid filter model to flat object
      const newFilters = {};
      Object.entries(filterModel).forEach(([key, value]) => {
        if (value.values) {
          newFilters[key] = value.values;
        } else if (value.value !== undefined) {
          newFilters[key] = value.value;
        }
      });

      // Clear filters that were removed (skip excluded filters like searchText)
      Object.keys(filters).forEach(key => {
        if (excludeFilters.includes(key)) return;
        if (!filterModel[key]) {
          if (Array.isArray(filters[key])) {
            newFilters[key] = [];
          } else if (typeof filters[key] === 'boolean' || filters[key] === null) {
            newFilters[key] = null;
          } else {
            newFilters[key] = '';
          }
        }
      });

      onFiltersChange?.(newFilters);
    },
    [filters, excludeFilters, onFiltersChange],
  );

  const handleSortChanged = useCallback(
    event => {
      const columnState = event.api.getColumnState();
      const sortedColumn = columnState.find(col => col.sort);

      const sortInfo = sortedColumn
        ? { sortBy: sortedColumn.colId, sortOrder: sortedColumn.sort }
        : { sortBy: '', sortOrder: '' }; // Clear both sortBy and sortOrder when no sort

      onSortChange?.(sortInfo);
    },
    [onSortChange],
  );

  const handlePaginationChanged = useCallback(
    event => {
      // Skip if this is a programmatic pagination sync
      if (isSettingPaginationRef.current) return;

      const currentPage = event.api.paginationGetCurrentPage() + 1; // AG Grid is 0-indexed
      const pageSize = event.api.paginationGetPageSize();

      onPaginationChange?.({ page: currentPage, pageSize });
    },
    [onPaginationChange],
  );

  return {
    // Table ref
    tableRef,

    // AG Grid event handlers
    handleGridReady,
    handleFilterChanged,
    handleSortChanged,
    handlePaginationChanged,
  };
}
